/**
 * Per-turn code chip for the paused-for-review review UX.
 *
 * Click target stays the same as the read-only flow — opens the
 * CodeDetailsModal so the researcher can drill into every place the
 * code was applied. Hovering the chip reveals a menu (same three
 * options as before; hover-triggered now so the click isn't hijacked
 * by the review controls):
 *
 *   - Unassign from this passage (turn-scoped)
 *   - Rename code (global; opens inline form Popover)
 *   - Delete code from analysis (global; loud confirm Popover)
 *
 * Hover lifecycle: opening on chip-enter, closing 200ms after chip-leave
 * unless the mouse enters the menu within that grace window. That way
 * the natural "move from chip → menu item" gesture doesn't drop the
 * menu mid-traversal.
 */
import { useRef, useState } from 'react';

import { DeleteOutline, EditOutlined, LinkOffOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popover,
  Popper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useDeleteCode, useRenameCode, useUnassignCodeFromTurn } from '@hooks/useCodeReview';

const HOVER_CLOSE_DELAY_MS = 200;

export default function TurnCodeChip({
  studyId,
  analysisItemId,
  qualitativeItemId,
  turnId,
  codeName,
  definition = '',
  isActive,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  onCodeClick,
}) {
  const renameMutation = useRenameCode(studyId, analysisItemId);
  const deleteMutation = useDeleteCode(studyId, analysisItemId);
  const unassignMutation = useUnassignCodeFromTurn(studyId, analysisItemId);

  const chipRef = useRef(null);
  const closeTimerRef = useRef(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [renameAnchorEl, setRenameAnchorEl] = useState(null);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [draftName, setDraftName] = useState(codeName);
  const [draftDefinition, setDraftDefinition] = useState(definition || '');

  const cancelScheduledClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelScheduledClose();
    closeTimerRef.current = setTimeout(() => {
      setMenuOpen(false);
      closeTimerRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  };

  const handleChipEnter = (e) => {
    cancelScheduledClose();
    setMenuOpen(true);
    onMouseEnter?.(e);
  };
  const handleChipLeave = (e) => {
    scheduleClose();
    onMouseLeave?.(e);
  };
  const handleMenuEnter = () => {
    cancelScheduledClose();
    setMenuOpen(true);
  };
  const handleMenuLeave = () => {
    scheduleClose();
  };

  const closeMenuImmediate = () => {
    cancelScheduledClose();
    setMenuOpen(false);
  };

  const openRename = (evt) => {
    evt.stopPropagation();
    setDraftName(codeName);
    setDraftDefinition(definition || '');
    // Anchor to the chip itself so the popover lines up nicely whether
    // the user clicked the menu item or hover-triggered it from a
    // narrow chip.
    setRenameAnchorEl(chipRef.current);
    closeMenuImmediate();
  };
  const closeRename = () => setRenameAnchorEl(null);

  const openDelete = (evt) => {
    evt.stopPropagation();
    setDeleteAnchorEl(chipRef.current);
    closeMenuImmediate();
  };
  const closeDelete = () => setDeleteAnchorEl(null);

  const handleUnassign = (evt) => {
    evt.stopPropagation();
    unassignMutation.mutate({ qualitativeItemId, code: codeName, turnId });
    closeMenuImmediate();
  };

  const handleRenameSubmit = (evt) => {
    evt?.preventDefault?.();
    const next = draftName.trim();
    if (!next || next === codeName) {
      closeRename();
      return;
    }
    renameMutation.mutate(
      {
        oldCode: codeName,
        newCode: next,
        definition:
          draftDefinition.trim() && draftDefinition.trim() !== (definition || '').trim()
            ? draftDefinition.trim()
            : undefined,
      },
      { onSuccess: closeRename },
    );
  };

  const handleDelete = () => {
    deleteMutation.mutate({ code: codeName }, { onSuccess: closeDelete });
  };

  const isBusy = renameMutation.isPending || deleteMutation.isPending || unassignMutation.isPending;

  return (
    <>
      <Box
        ref={chipRef}
        onMouseEnter={handleChipEnter}
        onMouseLeave={handleChipLeave}
        sx={{ display: 'inline-flex' }}
      >
        <Chip
          size="small"
          label={
            isBusy ? (
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <CircularProgress size={10} />
                <span>{codeName}</span>
              </Stack>
            ) : (
              codeName
            )
          }
          variant={isActive ? 'filled' : 'outlined'}
          color={isActive ? 'primary' : 'default'}
          clickable
          onClick={() => onCodeClick?.({ name: codeName, definition })}
          onFocus={onFocus}
          onBlur={onBlur}
          sx={{ cursor: 'pointer' }}
          disabled={isBusy}
        />
      </Box>

      {/* Hover-triggered action menu. Mouse enter on chip opens, mouse
          leave schedules a 200 ms close that the menu itself can cancel
          when the mouse moves into it. Keeps the natural "hover then
          click an item" gesture intact. */}
      <Popper
        open={menuOpen && Boolean(chipRef.current) && !renameAnchorEl && !deleteAnchorEl}
        anchorEl={chipRef.current}
        placement="bottom-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
        style={{ zIndex: 1300 }}
      >
        <Paper
          elevation={4}
          onMouseEnter={handleMenuEnter}
          onMouseLeave={handleMenuLeave}
          sx={{ minWidth: 240 }}
        >
          <MenuList dense>
            <MenuItem onClick={handleUnassign}>
              <ListItemIcon>
                <LinkOffOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Unassign from this passage"
                secondary="Removes only this turn's assignment"
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </MenuItem>
            <MenuItem onClick={openRename}>
              <ListItemIcon>
                <EditOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Rename code"
                secondary="Updates everywhere it appears"
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </MenuItem>
            <MenuItem onClick={openDelete}>
              <ListItemIcon>
                <DeleteOutline fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" color="error.main">
                    Delete code from analysis
                  </Typography>
                }
                secondary="Removes it from every passage"
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </MenuItem>
          </MenuList>
        </Paper>
      </Popper>

      {/* Rename popover */}
      <Popover
        open={Boolean(renameAnchorEl)}
        anchorEl={renameAnchorEl}
        onClose={closeRename}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box component="form" onSubmit={handleRenameSubmit} sx={{ p: 2, width: 320 }}>
          <Typography variant="subtitle2" gutterBottom>
            Rename code
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            Renaming updates this code everywhere it&apos;s applied across the analysis.
          </Typography>
          <TextField
            label="Code name"
            size="small"
            fullWidth
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- popover input
            autoFocus
            sx={{ mb: 1.5 }}
          />
          <TextField
            label="Definition (optional)"
            size="small"
            fullWidth
            value={draftDefinition}
            onChange={(e) => setDraftDefinition(e.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            placeholder="Leave blank to keep the current definition."
            sx={{ mb: 1.5 }}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={closeRename} disabled={renameMutation.isPending}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              type="submit"
              disabled={!draftName.trim() || renameMutation.isPending}
            >
              {renameMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Box>
      </Popover>

      {/* Delete confirm */}
      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={closeDelete}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2" gutterBottom>
            Delete &quot;{codeName}&quot; from the analysis?
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            This removes the code from every passage where it&apos;s applied. You can&apos;t undo
            it.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={closeDelete} disabled={deleteMutation.isPending}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
